export type PerSale =
  /**
   * No sales to divide by, so there is no rate.
   *
   * Distinct from a rate of zero on purpose. Zero is a real answer - forty sales and no refunds - and using it
   * for "nothing was sold" tells a producer their refund rate is excellent when in fact nothing happened. The
   * same collision the MRR coverage work had to fix, on a different figure.
   */
  { kind: 'none' } | { kind: 'value'; value: number };

/**
 * Anything measured PER SALE, with the denominator guarded in one place.
 *
 * Two cards need it and both divide by the sale count: the refund rate is refunds over sales, and the average
 * ticket is net value over sales. The numbers were already on the root dashboard in four separate cards and
 * nobody did either division - a producer read "40 vendas" and "3 reembolsos" and worked it out, or did not.
 *
 * Not `resolveVariation`, which answers a different question - period over period - and is used beside this for
 * the badge. Hand-dividing is what this exists to stop: the inline version of the variation produced Infinity on
 * an empty previous period and NaN on two, and both got papered over as though they were measurements.
 */
export const resolvePerSale = (
  numerator: number,
  salesCount: number,
): PerSale => {
  if (!Number.isFinite(numerator) || !Number.isFinite(salesCount))
    return { kind: 'none' };

  if (salesCount <= 0) return { kind: 'none' };

  return { kind: 'value', value: numerator / salesCount };
};

/**
 * The API's own figure when it sent one, computed locally when it did not, and NEITHER when nothing was sold.
 *
 * report-api derives refundRate and averageTicket precisely so the front stops dividing - its comment says so -
 * and this repo simply never declared the fields. So the reported value wins.
 *
 * The sales count decides the `none` case regardless of what arrived, because the API cannot express it: its own
 * comment says "a producer with no sale in the period must get 0", and 0 is exactly the value that collides with a
 * real answer. A refund rate of 0 means forty sales and no refunds; it must not also mean nothing was sold. The
 * API is right for its own contract - a number field has to hold a number - and the screen is where the
 * distinction has to be made.
 */
export const resolveReportedPerSale = (
  reported: number | string | undefined,
  numerator: number,
  salesCount: number,
): PerSale => {
  if (!Number.isFinite(salesCount) || salesCount <= 0) return { kind: 'none' };

  const value = Number(reported);
  if (reported !== undefined && reported !== null && Number.isFinite(value))
    return { kind: 'value', value };

  return resolvePerSale(numerator, salesCount);
};

/**
 * Whether a badge may be drawn at all: BOTH sides have to exist.
 *
 * An absent current rate must suppress the comparison rather than becoming a zero. Mapping `none` to 0 made a
 * period with no sales report a 100% DECREASE against a previous period that had a rate - a badge inventing a
 * change for a figure the card beside it is simultaneously refusing to state. Codex caught it on #265.
 *
 * A previous absence is different and stays: resolveVariation reads a previous 0 as "novo", which is the honest
 * answer for a first period with sales.
 */
export const canCompare = (current: PerSale) => current.kind === 'value';

/** The comparable number, once canCompare has said there is one. A missing previous period reads as zero. */
export const perSaleForComparison = (perSale: PerSale) =>
  perSale.kind === 'value' ? perSale.value : 0;
